import React, { useState } from "react";

const LANG_NAMES = {
  en:"English",af:"Afrikaans",zu:"isiZulu",xh:"isiXhosa",st:"Sesotho",
  es:"Spanish",fr:"French",pt:"Portuguese",ar:"Arabic",zh:"Mandarin",
  hi:"Hindi",de:"German",nl:"Dutch",sw:"Swahili",ha:"Hausa",yo:"Yoruba",
};

// ─── Prompt: intake answers → personalised 8-milestone plan ──────────────────
export function buildIntakePlanPrompt({ a1, a2, a3, language, countryName }) {
  const langName = LANG_NAMES[language || "en"] || "English";
  return `You are SolFri, a warm compassionate mental health companion. Design a personalised emotional wellness journey for a user in ${countryName || "their country"} based on their intake answers:

1. What brings them here: "${a1}"
2. What feeling better would look like for them: "${a2}"
3. What they've tried before and what gets in the way: "${a3}"

Create EXACTLY 8 milestones ordered therapeutically: first stabilise and build safety, then understand their patterns, then practise new responses, then integrate and sustain. Every milestone must be specific to THIS person's own words — never generic self-help titles.

Respond ONLY with valid JSON, every field written in ${langName}:
{
  "summary": "2 warm sentences describing the arc of their journey, addressed to them directly",
  "milestones": [
    { "title": "3-6 words", "description": "1-2 sentences on what you'll work on together in this milestone", "focus": "one-word theme" }
  ]
}
Exactly 8 milestones. No markdown. Only JSON.`;
}

// ─── Addendum appended to the session prompt when in journey mode ─────────────
export function buildJourneyAddendum(journey, language) {
  const langName = LANG_NAMES[language || "en"] || "English";
  const i = journey.currentIndex;
  if (i >= journey.milestones.length) return "";
  const m = journey.milestones[i];
  return `\n\nJOURNEY MODE — the user chose a guided journey session. They are on milestone ${i + 1} of ${journey.milestones.length}: "${m.title}" — ${m.description}. Gently guide the conversation toward this focus while still meeting them where they are today. Judge honestly whether this exchange genuinely advanced the milestone — showing up alone is not progress; comfortable avoidance is not progress. ADD this field to your JSON: "milestone_progress": {"status": "completed" | "progressing" | "not_engaged", "note": "1 warm honest sentence in ${langName} about where they stand with this milestone"}. Mark "completed" ONLY when the milestone's aim has truly been met across the conversation. If what you have learned in this or past sessions means the REMAINING milestones should change to serve them better, also add "journey_adjustment": [{"index": <0-based position in the full milestone list, must be greater than ${i}>, "title": "...", "description": "..."}] — adjust only future milestones, and only when genuinely warranted.`;
}

// ─── Home-screen journey card ─────────────────────────────────────────────────
export function JourneyCard({ journey, onOpen }) {
  if (!journey) {
    return (
      <div style={{background:"linear-gradient(135deg,#F5EDE4,#FDF8F3)",borderRadius:16,padding:"16px 18px",border:"1.5px solid #E8D5C4",marginBottom:16}}>
        <p style={{fontSize:14,fontWeight:800,color:"#5C3D2E",margin:"0 0 4px"}}>🧭 Begin your Journey</p>
        <p style={{fontSize:12,color:"#A08070",lineHeight:1.6,margin:"0 0 12px"}}>
          A personalised path of 8 milestones, built around your life — and it adapts as SolFri gets to know you.
        </p>
        <button onClick={onOpen} style={{background:"#A0673A",color:"#fff",border:"none",borderRadius:10,padding:"10px 18px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>
          Create my journey →
        </button>
      </div>
    );
  }

  const total = journey.milestones.length;
  const done = journey.currentIndex;
  const complete = done >= total;
  const current = complete ? null : journey.milestones[done];
  const pct = Math.round((done / total) * 100);

  return (
    <div style={{background:"#fff",borderRadius:16,padding:"14px 18px",border:"1px solid #F0E0D0",marginBottom:16,cursor:"pointer"}} onClick={onOpen}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
        <p style={{fontSize:13,fontWeight:800,color:"#5C3D2E",margin:0}}>🧭 Your Journey</p>
        <span style={{fontSize:11,color:"#A08070",fontWeight:600}}>{done} of {total} milestones</span>
      </div>
      <div style={{background:"#F5EDE4",borderRadius:8,height:8,overflow:"hidden",marginBottom:10}}>
        <div style={{background:"#7A9E7E",height:8,width:`${pct}%`,borderRadius:8,transition:"width 0.6s ease"}}/>
      </div>
      {complete
        ? <p style={{fontSize:12,color:"#7A9E7E",fontWeight:700,margin:0}}>🌳 Journey complete — look how far you've come. Tap to start a new one.</p>
        : <p style={{fontSize:12,color:"#A08070",margin:0}}>Up next: <strong style={{color:"#A0673A"}}>{current.title}</strong> — tap to view or start a journey session</p>
      }
    </div>
  );
}

// ─── Full journey modal: intake form OR milestone path ────────────────────────
export function JourneyModal({ journey, onClose, onCreate, onStartMilestone, onReset, creating, error }) {
  const [a1, setA1] = useState("");
  const [a2, setA2] = useState("");
  const [a3, setA3] = useState("");

  const ta = (val, set, placeholder) => (
    <textarea
      value={val} onChange={e=>set(e.target.value)} placeholder={placeholder} rows={2}
      style={{width:"100%",border:"1.5px solid #E8D5C4",borderRadius:10,padding:"10px 13px",fontSize:13,lineHeight:1.6,color:"#1E293B",resize:"none",outline:"none",fontFamily:"inherit",boxSizing:"border-box",background:"#FFFAF7",marginBottom:12}}
      onFocus={e=>e.target.style.borderColor="#A0673A"} onBlur={e=>e.target.style.borderColor="#E8D5C4"}
    />
  );

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(15,23,42,0.5)",zIndex:150,display:"flex",alignItems:"flex-end",justifyContent:"center"}} onClick={onClose}>
      <div onClick={e=>e.stopPropagation()} style={{background:"#FDF8F3",borderRadius:"22px 22px 0 0",width:"100%",maxWidth:560,maxHeight:"85vh",display:"flex",flexDirection:"column"}}>
        <div style={{padding:"20px 20px 14px",display:"flex",justifyContent:"space-between",alignItems:"center",borderBottom:"1px solid #F0E0D0",flexShrink:0}}>
          <div>
            <h2 style={{fontSize:18,fontWeight:800,color:"#5C3D2E",margin:0}}>🧭 {journey ? "Your Journey" : "Create your Journey"}</h2>
            {journey && <p style={{fontSize:11,color:"#A08070",margin:"2px 0 0"}}>{Math.min(journey.currentIndex, journey.milestones.length)} of {journey.milestones.length} milestones complete — your path adapts as you grow</p>}
          </div>
          <button onClick={onClose} style={{background:"#F0E0D0",border:"none",borderRadius:"50%",width:32,height:32,cursor:"pointer",fontSize:16,color:"#A08070",display:"flex",alignItems:"center",justifyContent:"center"}}>✕</button>
        </div>

        <div style={{overflowY:"auto",padding:"16px 20px 28px",flex:1}}>

          {/* ── Intake form ── */}
          {!journey && (
            <>
              <p style={{fontSize:13,color:"#475569",lineHeight:1.7,marginBottom:16}}>
                Three honest answers, and SolFri will build a path of 8 milestones just for you. It's not a fixed programme — as you have sessions, your journey adapts to what actually comes up. 🌳
              </p>
              <p style={{fontSize:12,fontWeight:700,color:"#5C3D2E",marginBottom:6}}>1. What brings you here right now?</p>
              {ta(a1, setA1, "Work is crushing me… my relationship… I can't sleep…")}
              <p style={{fontSize:12,fontWeight:700,color:"#5C3D2E",marginBottom:6}}>2. What would feeling better look like for you?</p>
              {ta(a2, setA2, "Waking up without dread… being present with my kids…")}
              <p style={{fontSize:12,fontWeight:700,color:"#5C3D2E",marginBottom:6}}>3. What have you tried, and what gets in the way?</p>
              {ta(a3, setA3, "I journal sometimes but stop… I bottle it up until I explode…")}
              {error && <p style={{color:"#EF4444",fontSize:12,marginBottom:10}}>⚠️ {error}</p>}
              <button
                onClick={()=>onCreate({ a1: a1.trim(), a2: a2.trim(), a3: a3.trim() })}
                disabled={creating || !a1.trim() || !a2.trim() || !a3.trim()}
                style={{width:"100%",background:creating||!a1.trim()||!a2.trim()||!a3.trim()?"#CBD5E1":"#A0673A",color:"#fff",border:"none",borderRadius:12,padding:"13px 0",fontSize:14,fontWeight:700,cursor:creating?"wait":"pointer",fontFamily:"inherit"}}
              >
                {creating ? "SolFri is designing your path…" : "Build my journey →"}
              </button>
            </>
          )}

          {/* ── Milestone path ── */}
          {journey && (
            <>
              <p style={{fontSize:13,color:"#475569",fontStyle:"italic",lineHeight:1.7,marginBottom:18}}>{journey.summary}</p>
              {journey.milestones.map((m, i) => {
                const state = i < journey.currentIndex ? "done" : i === journey.currentIndex ? "current" : "upcoming";
                return (
                  <div key={i} style={{display:"flex",gap:12,marginBottom:4}}>
                    {/* Path column */}
                    <div style={{display:"flex",flexDirection:"column",alignItems:"center",width:30,flexShrink:0}}>
                      <div style={{
                        width:28,height:28,borderRadius:"50%",display:"flex",alignItems:"center",justifyContent:"center",
                        fontSize:12,fontWeight:800,flexShrink:0,
                        background: state==="done" ? "#7A9E7E" : state==="current" ? "#fff" : "#F5EDE4",
                        border: state==="current" ? "2.5px solid #A0673A" : "2px solid " + (state==="done" ? "#7A9E7E" : "#E8D5C4"),
                        color: state==="done" ? "#fff" : state==="current" ? "#A0673A" : "#C4A882",
                      }}>
                        {state==="done" ? "✓" : i + 1}
                      </div>
                      {i < journey.milestones.length - 1 && (
                        <div style={{width:2,flex:1,minHeight:16,background: state==="done" ? "#7A9E7E" : "#EDE0D2"}}/>
                      )}
                    </div>
                    {/* Content */}
                    <div style={{paddingBottom:14,flex:1,opacity: state==="upcoming" ? 0.65 : 1}}>
                      <p style={{fontSize:13,fontWeight:700,color: state==="current" ? "#A0673A" : "#5C3D2E",margin:"4px 0 2px"}}>
                        {m.title}{m.adjusted && <span style={{fontSize:10,color:"#7A9E7E",fontWeight:600}}> · adapted for you</span>}
                      </p>
                      <p style={{fontSize:12,color:"#A08070",lineHeight:1.55,margin:0}}>{m.description}</p>
                      {state==="current" && (
                        <button onClick={onStartMilestone} style={{marginTop:8,background:"#A0673A",color:"#fff",border:"none",borderRadius:9,padding:"8px 16px",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>
                          Work on this with SolFri →
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
              <button onClick={onReset} style={{marginTop:12,background:"none",border:"none",color:"#C4A882",fontSize:11,cursor:"pointer",fontFamily:"inherit",textDecoration:"underline",padding:0}}>
                Start a completely new journey
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
